import type { JSX, PropsWithChildren } from 'react';

import { Col, Radio, Row } from 'antd';
import { isString } from 'lodash';

import { MFormItemError } from '../MFormItemError/MFormItemError';
import { MRadio } from '../MRadio/MRadio';
import { MTooltip } from '../MTooltip/MTooltip';

import * as Styles from './MRadioGroup.styles';

const { Group } = Radio;

export interface MRadioGroupOption {
  label: string | JSX.Element;
  value: string;
  description?: string | JSX.Element;
  alert?: string | JSX.Element;
  tooltip?: {
    type: 'modal' | 'popover';
    title?: string;
    content?: string;
    icon?: 'info' | 'question';
  };
}

export interface MRadioGroupProps {
  value?: string;
  defaultValue?: string;
  onChange: (value: string) => void;
  options: MRadioGroupOption[];
  disabled?: boolean;
  error?: string;
  align?: 'horizontal' | 'vertical';
  testId?: string;
}

export const MRadioGroup = ({
  options,
  value,
  defaultValue,
  onChange,
  error,
  align = 'horizontal',
  testId,
}: PropsWithChildren<MRadioGroupProps>) => {
  const getTestId = (value: string | JSX.Element) => {
    if (testId && isString(value)) {
      return testId + '-' + value.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
    }
  };

  return (
    <Row>
      <Col span={24}>
        <Group className={Styles.group(align)} defaultValue={defaultValue} data-testid={testId}>
          {options.map(anOption => (
            <MRadio
              key={anOption.value}
              value={anOption.value}
              onChange={onChange}
              alert={anOption.value === value && anOption.alert ? anOption.alert : undefined}
              description={anOption.description}
              className={Styles.button}
              testId={getTestId(anOption.value)}>
              <span data-testid={getTestId(anOption.value) + '-text'}>{anOption.label}</span>
              {anOption.tooltip && (
                <MTooltip type={anOption.tooltip.type} title={anOption.tooltip.title} icon={anOption.tooltip.icon}>
                  {anOption.tooltip.content}
                </MTooltip>
              )}
            </MRadio>
          ))}
        </Group>
      </Col>
      <Col span={24}>
        <MFormItemError value={error} testId={testId + '-error-message'} />
      </Col>
    </Row>
  );
};
